import React from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { DatePicker, FormField, Select, TextField } from "@outpave/ds";
import { Stack } from "components/layout/reusableLayouts";
import { useMask } from "@react-input/mask";
import { FormikProps } from "formik";
import { useFormikHelpers } from "hooks/useFormikHelpers";
import Role from "../../../enums/Role";
import { useAssignableRoles } from "hooks/useAssignableRoles";
import { useFeatureEntitlements } from "@frontegg/react";
import StatusType from "../../../enums/UserStatus";
import EmployeeSearchAutoComplete from "../../common/EmployeeSearchAutoComplete";
import { FormikAddUser } from "hooks/useUserProfileData";
import dayjs from "utils/dayjs";
import FormikCustomFields from "components/common/FormikCustomFields";
import { FormikCustomField } from "hooks/customFields.types";
import LocationSearchAutoComplete from "components/common/LocationSearchAutoComplete";
import ConfigureAddressDrawer from "components/common/ConfigureAddressDrawer";
import AddressField from "components/common/address/AddressField";

interface UserInformationProps {
  formik: FormikProps<FormikAddUser>;
  customFieldSettings?: FormikCustomField[];
  /** Your own record, below Admin: only what updateMyProfile saves is editable. */
  self?: boolean;
  /**
   * Takes the company default the location picker fills in on an untouched
   * form, for a form whose `enableReinitialize` would undo a `resetForm`.
   */
  onDefaultLocation?: (location: FormikAddUser["location"]) => void;
}

const UserInformation: React.FC<UserInformationProps> = (props) => {
  const {
    formik,
    customFieldSettings,
    self = false,
    onDefaultLocation,
  } = props;
  const { isEntitled: featureCardholder } =
    useFeatureEntitlements("feature_cardholder");

  const showCardholderOption =
    featureCardholder || formik.values.role === Role.Cardholder;

  // Company-level roles only; platform roles are granted from the platform app.
  const companyRoles: Role[] = [
    ...(showCardholderOption ? [Role.Cardholder] : []),
    Role.Employee,
    Role.Contractor,
    Role.Manager,
    Role.Auditor,
    Role.Accountant,
    Role.Admin,
  ];
  const { options: roleOptions, fieldDisabled: roleFieldDisabled } =
    useAssignableRoles(companyRoles, formik.values.role as Role);

  const inputRef = useMask({ mask: "___-___-____", replacement: { _: /\d/ } });
  const { t } = useTranslation();
  const formikHelpers = useFormikHelpers(formik);

  const [openAddressDrawer, setOpenAddressDrawer] = React.useState(false);
  const { isEntitled: featureCustomFields } = useFeatureEntitlements(
    "feature_card_custom_fields",
  );

  return (
    <Stack gap="var(--space-7)">
      <TextField
        name="firstName"
        label={t("First Name")}
        required={true}
        value={formik.values.firstName}
        fullWidth
        onChange={formik.handleChange}
        error={formikHelpers.handleFormikError("firstName")}
        helperText={formikHelpers.handleFormikHelperText("firstName")}
      />

      <TextField
        name="lastName"
        label={t("Last Name")}
        required={true}
        value={formik.values.lastName}
        fullWidth
        onChange={formik.handleChange}
        error={formikHelpers.handleFormikError("lastName")}
        helperText={formikHelpers.handleFormikHelperText("lastName")}
      />

      <TextField
        name="email"
        label={t("Email Address")}
        required={true}
        disabled={
          self ||
          (!!formik?.values?.id &&
            [StatusType.ACTIVE, StatusType.PENDING].includes(
              formik.values.status as StatusType,
            ))
        }
        value={formik.values.email}
        fullWidth
        onChange={formik.handleChange}
        error={formikHelpers.handleFormikError("email")}
        helperText={formikHelpers.handleFormikHelperText("email")}
      />

      <TextField
        ref={inputRef}
        name="phone"
        label={t("Phone Number")}
        required={true}
        fullWidth
        value={formik.values.phone}
        onChange={formik.handleChange}
        error={formikHelpers.handleFormikError("phone")}
        helperText={
          formikHelpers.handleFormikHelperText("phone") ||
          t("PhoneNumberDescription")
        }
      />

      <AddressField
        name="address"
        label={t("Address")}
        required
        address={formik.values.address}
        onOpen={() => setOpenAddressDrawer(true)}
        errorText={
          formikHelpers.handleFormikHelperText("address", { deep: true }) ??
          undefined
        }
        helperText={t("AddAddressDescription")}
      />

      <LocationSearchAutoComplete
        label={t("Business Location")}
        location={formik.values?.location?.id ? formik.values.location : null}
        onSelectLocation={(selectedLocation) => {
          formik.setFieldValue("location", {
            id: selectedLocation?.id || "",
            addressName: selectedLocation?.addressName || "",
          });
        }}
        // An untouched form takes the default as its starting value, so it stays clean.
        onDefaultLocation={({ id, addressName }) => {
          const location = { id, addressName };
          if (formik.dirty) {
            formik.setFieldValue("location", location);
            return;
          }
          if (onDefaultLocation) {
            onDefaultLocation(location);
            return;
          }
          formik.resetForm({
            values: { ...formik.values, location },
            touched: formik.touched,
            errors: formik.errors,
          });
        }}
      />

      {/* The DS picker owns the field and calendar but carries no label,
          helper or required marker, so FormField supplies them. It wraps its
          own LocalizationProvider, so the app no longer has to. */}
      <FormField
        label={t("Date of Birth")}
        required={true}
        error={formikHelpers.handleFormikError("birthdate")}
        helperText={
          formikHelpers.handleFormikHelperText("birthdate") ||
          t("The user must be at least 18 years of age")
        }
      >
        <DatePicker
          aria-label={t("Date of Birth")}
          // An unset birthdate is null, not dayjs(""): the old unguarded call
          // put an Invalid Date in the field before the user had picked one.
          value={
            formik.values.birthdate ? dayjs(formik.values.birthdate) : null
          }
          onChange={(value) => {
            // A Date, not a formatted string: FormikAddUser types birthdate as
            // `Date | null`, submit does `dayjs(birthdate).valueOf()`, and
            // dayjs("") is NaN — which a cleared or half-typed date would
            // otherwise send. `!birthdate` still reads as missing either way.
            formik.setFieldValue("birthdate", value ? value.toDate() : null);
          }}
          maxDate={dayjs().subtract(18, "year")}
          error={formikHelpers.handleFormikError("birthdate")}
        />
      </FormField>

      {!self && (
        <>
          <Select
            label={t("User role")}
            required={true}
            fullWidth
            value={formik.values.role}
            onChange={(id) => formik.setFieldValue("role", id)}
            disabled={roleFieldDisabled}
            // handleFormikError returns a boolean, which the field would read as
            // its error *text* and render as nothing; the helper carries the copy.
            errorText={
              formikHelpers.handleFormikHelperText("role") || undefined
            }
            options={roleOptions.map(({ role, disabled }) => ({
              id: role,
              label: t(role),
              disabled,
            }))}
          />

          <EmployeeSearchAutoComplete
            title={t("Assign a Manager")}
            employee={
              formik.values?.assignedManager?.id
                ? formik.values.assignedManager
                : null
            }
            handleSelectEmployee={(selectedEmployee) => {
              formik.setFieldValue("assignedManager", {
                id: selectedEmployee?.id || "",
                firstName: selectedEmployee?.firstName || "",
                lastName: selectedEmployee?.lastName || "",
                email: selectedEmployee?.email || "",
              });
            }}
            additionalVariables={{
              role: [Role.Manager, Role.Admin],
            }}
          />
        </>
      )}

      {featureCustomFields && (
        <FormikCustomFields
          settings={customFieldSettings}
          formik={formik}
          variant="flat"
        />
      )}

      <ConfigureAddressDrawer
        open={openAddressDrawer}
        drawerTitle={[t("User Address")]}
        onClose={() => setOpenAddressDrawer(false)}
        onSuccess={async (values) => {
          formik.setFieldValue("address", {
            ...formik.values.address,
            ...values,
          });

          setOpenAddressDrawer(false);
        }}
        initialValues={formik.values.address}
      />
    </Stack>
  );
};

export default UserInformation;
